import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { SearchIcon } from "./Icons";

/**
 * Multi-select team-member picker with a search box and a capped menu height.
 *
 * Replaces a plain `Select multiple` whose `renderValue` joined every selected
 * name with commas: the menu grew without bound on a large team, and the
 * trigger text overflowed its quarter-width grid cell.
 *
 * Member objects (not ids) are the values, because callers read
 * `mobile_number`, `user_name`, `_id` and `playerDetails` straight off the
 * selection. MUI compares option values by identity, so selection state is
 * tracked by `_id` instead.
 */
const SearchableMemberSelect = ({
  members = [],
  value = [],
  onChange,
  placeholder = "Select Players",
  maxMenuHeight = 340,
  disabled = false,
  // Members who have left the team. Selectable — their historical records are
  // still reportable — but listed after current members and tagged, so a
  // leader doesn't mistake them for someone still on the team.
  formerMembers = [],
}) => {
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  // Current members first, former members after — one list so search,
  // select-all and the height cap all behave as before.
  const allMembers = useMemo(
    () => [...members, ...formerMembers],
    [members, formerMembers]
  );
  const formerIds = useMemo(
    () => new Set(formerMembers.map((m) => m._id)),
    [formerMembers]
  );

  // A stale query would hide the new team's members, so reset whenever the
  // underlying list changes — the report type and team pickers both clear the
  // selection but know nothing about this component's internal state.
  useEffect(() => {
    setSearch("");
  }, [allMembers]);

  // Matching ids rather than a filtered list: every member stays mounted as a
  // MenuItem and non-matches are just hidden. Unmounting them would drop the
  // options backing any selected-but-filtered-out member, and MUI would then
  // warn that `value` is out of range.
  const matchingIds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null; // null = everything matches
    return new Set(
      allMembers
        .filter(
          (m) =>
            (m?.user_name || "").toLowerCase().includes(q) ||
            (m?.mobile_number || "").toLowerCase().includes(q)
        )
        .map((m) => m._id)
    );
  }, [allMembers, search]);

  const matchCount = matchingIds ? matchingIds.size : allMembers.length;

  const isChecked = (member) => value.some((m) => m._id === member._id);

  const handleToggle = (member) => {
    if (isChecked(member)) {
      onChange(value.filter((m) => m._id !== member._id));
    } else {
      onChange([...value, member]);
    }
  };

  const allSelected = allMembers.length > 0 && value.length === allMembers.length;

  return (
    <Select
      multiple
      displayEmpty
      disabled={disabled}
      sx={{
        width: "100%",
        background: "white",
        color: value.length > 0 ? "black" : "#b3b3b3",
      }}
      value={value}
      size="small"
      // Selection is driven entirely by the rows' own onClick so that the
      // search field and the select-all button can live inside the menu
      // without MUI treating them as options.
      onChange={() => {}}
      MenuProps={{
        autoFocus: false,
        PaperProps: { sx: { maxHeight: maxMenuHeight } },
      }}
      onClose={() => setSearch("")}
      renderValue={(selected) => {
        if (!selected || selected.length === 0) return placeholder;
        if (selected.length === 1) return selected[0]?.user_name;
        return `${selected.length} members selected`;
      }}
    >
      {/* Search box. Not a MenuItem: MUI's Select intercepts keystrokes for
          type-ahead and would steal focus from the input on every character. */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: "white",
          px: 1.5,
          pt: 1,
          pb: 1,
        }}
        // Swallow keystrokes so MUI's Select type-ahead doesn't consume them
        // and yank focus out of the input — but let Escape and Tab through, or
        // the menu can no longer be dismissed or exited from the keyboard.
        onKeyDown={(e) => {
          if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          autoFocus
          placeholder="Search name or number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {allMembers.length > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              {value.length} of {allMembers.length} selected
            </Typography>
            <Button
              size="small"
              onClick={() => onChange(allSelected ? [] : [...allMembers])}
              sx={{ minWidth: 0, textTransform: "none" }}
            >
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
          </Box>
        )}
      </Box>

      {matchCount === 0 && (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            No members match your search
          </Typography>
        </Box>
      )}

      {allMembers.map((member) => (
        <MenuItem
          key={member._id}
          value={member}
          onClick={() => handleToggle(member)}
          sx={
            matchingIds && !matchingIds.has(member._id)
              ? { display: "none" }
              : undefined
          }
        >
          <Checkbox checked={isChecked(member)} size="small" />
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>{member?.user_name}</span>
                {formerIds.has(member._id) && (
                  <Chip label="Former" size="small" sx={{ height: 18, fontSize: 11 }} />
                )}
              </Box>
            }
            secondary={member?.mobile_number || undefined}
          />
        </MenuItem>
      ))}
    </Select>
  );
};

export default SearchableMemberSelect;
